import React, { useEffect, useMemo, useState } from 'react'
import { Radio, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useRequest } from 'ahooks'
import { Styles } from 'jss'
import Button from '@/components/button/button'
import Tab, { TabItem } from '@/components/tab/tab'
import { Row } from '@/components/layout/layout'
import $msg from '@/utils/message'
import { Modal as SgModal } from '@/components/dialog/modal'
import { layerManage, showLayer } from '@/utils/layer'
import { deduplicateImportedLsMembers, getLsMemberImportInfo } from '../api'
import MemberNotice from '../member-notice'
import { getInitialRepeatTab, getRepeatChoiceText } from './repeat-member-utils'

interface Props {
    visible: boolean
    recordId?: number
    onCancel: () => void
    onSuccess: () => void
}

interface DuplicatePair {
    imported_member: any
    existed_member?: any
}

const confirmLayerStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 130,
    touchAction: 'none',
} as React.CSSProperties
const repeatModalStyles: Styles = {
    width: 'min(920px, calc(100vw - 32px))',
    minWidth: 'min(920px, calc(100vw - 32px))',
}
const getRepeatTabItemWidth = () => {
    if (typeof window === 'undefined') return 180

    return Math.min(180, Math.max(96, Math.floor((window.innerWidth - 64) / 3)))
}

const getMemberName = (member?: any) => member?.name || member?.realname || member?.nickname || '-'

const renderMemberInfo = (member?: any) => {
    if (!member) return '-'

    return (
        <div className="repeat-member__info">
            <div>{getMemberName(member)}</div>
            <div>手机号：{member.phone || '-'}</div>
            <div>卡号：{member.card_code || '-'}</div>
            <div>余额：{member.balance ?? '-'}</div>
            <div>积分：{member.points ?? member.point ?? '-'}</div>
        </div>
    )
}

const RepeatMemberHandler: React.FC<Props> = props => {
    const [choices, setChoices] = useState<{ [memberId: number]: number }>({})
    const [activeTab, setActiveTab] = useState(0)
    const [tabItemWidth, setTabItemWidth] = useState(getRepeatTabItemWidth)
    const infoReq = useRequest(() => getLsMemberImportInfo({ record_id: props.recordId }), {
        manual: true,
        onSuccess: res => {
            const nextChoices: { [memberId: number]: number } = {}
            const duplicatedMemberPairs = res?.duplicated_member_pairs || []

            duplicatedMemberPairs.forEach((pair: DuplicatePair) => {
                if (pair.existed_member?.id) {
                    nextChoices[pair.existed_member.id] = 0
                }
            })
            setChoices(nextChoices)
            setActiveTab(
                getInitialRepeatTab(
                    res?.duplicated_member_pairs || [],
                    res?.duplicated_card_code_member_pairs || [],
                    res?.duplicated_old_ls_pairs || [],
                ),
            )
        },
        onError: e => $msg('error', e.message || '获取重复会员信息失败'),
    })
    const req = useRequest(deduplicateImportedLsMembers, {
        manual: true,
        onSuccess: () => {
            $msg('success', '处理成功')
            props.onSuccess()
        },
        onError: e => $msg('error', e.message || '处理重复会员失败'),
    })

    useEffect(() => {
        if (!props.visible) {
            setChoices({})

            return
        }

        infoReq.run()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.visible, props.recordId])
    useEffect(() => {
        if (typeof document === 'undefined' || !props.visible) return

        document.body.classList.add('repeat-member-dialog-open')

        return () => document.body.classList.remove('repeat-member-dialog-open')
    }, [props.visible])
    useEffect(() => {
        if (typeof window === 'undefined') return

        const updateTabItemWidth = () => setTabItemWidth(getRepeatTabItemWidth())

        updateTabItemWidth()
        window.addEventListener('resize', updateTabItemWidth)

        return () => window.removeEventListener('resize', updateTabItemWidth)
    }, [])

    const duplicatedPairs: DuplicatePair[] = infoReq.data?.duplicated_member_pairs || []
    const cardCodePairs: DuplicatePair[] = infoReq.data?.duplicated_card_code_member_pairs || []
    const oldLsPairs: DuplicatePair[] = infoReq.data?.duplicated_old_ls_pairs || []
    const currentRecordId = props.recordId || infoReq.data?.record?.id
    const duplicateTabs: TabItem[] = useMemo(
        () => [
            { id: 0, name: `手机号/会员重复(${duplicatedPairs.length})` },
            { id: 1, name: `卡号重复(${cardCodePairs.length})` },
            { id: 2, name: `老零售重复(${oldLsPairs.length})` },
        ],
        [cardCodePairs.length, duplicatedPairs.length, oldLsPairs.length],
    )

    const setPairChoice = (memberId: number, source: number) =>
        setChoices(prev => ({
            ...prev,
            [memberId]: source,
        }))
    const renderSelectableMemberInfo = (pair: DuplicatePair, source: number) => {
        const memberId = pair.existed_member?.id
        const member = source === 1 ? pair.imported_member : pair.existed_member
        const checked = memberId ? (choices[memberId] ?? 0) === source : false
        const optionText = getRepeatChoiceText(source)

        if (!memberId) return renderMemberInfo(member)

        return (
            <div
                className={`repeat-member__choice ${checked ? 'repeat-member__choice--active' : ''}`}
                onClick={() => setPairChoice(memberId, source)}
            >
                {renderMemberInfo(member)}
                <Radio
                    className="repeat-member__choice-radio"
                    checked={checked}
                    onChange={() => setPairChoice(memberId, source)}
                >
                    {optionText}
                </Radio>
            </div>
        )
    }
    const normalColumns: ColumnsType<DuplicatePair> = [
        {
            title: '导入数据',
            dataIndex: 'imported_member',
            render: (_value, pair) => renderSelectableMemberInfo(pair, 1),
        },
        {
            title: '已有会员',
            dataIndex: 'existed_member',
            render: (_value, pair) => renderSelectableMemberInfo(pair, 0),
        },
    ]
    const readonlyColumns: ColumnsType<DuplicatePair> = [
        { title: '导入数据', dataIndex: 'imported_member', render: renderMemberInfo },
        { title: '冲突对象', dataIndex: 'existed_member', render: renderMemberInfo },
    ]

    const submit = (mode?: 'keep' | 'cover' | 'custom') => {
        if (!currentRecordId) return

        const dealDeduplicatedMembers =
            mode === 'custom'
                ? Object.keys(choices).map(memberId => ({
                      member_id: Number(memberId),
                      source: choices[Number(memberId)] || 0,
                  }))
                : duplicatedPairs
                      .filter(pair => pair.existed_member?.id)
                      .map(pair => ({
                          member_id: pair.existed_member.id,
                          source: 0,
                      }))

        showLayer(
            <SgModal
                headerStyles={{ width: '440px' }}
                isShow
                type="danger"
                style={{ height: '176px' }}
                okText="确认处理"
                onClose={() => layerManage.pop()}
                onCancel={() => layerManage.pop()}
                onConfirm={() => {
                    layerManage.pop()
                    req.run({
                        record_id: currentRecordId,
                        accept_imported_data: mode === 'cover' ? true : undefined,
                        deal_deduplicated_members: mode === 'cover' ? undefined : dealDeduplicatedMembers,
                    })
                }}
            >
                <div>
                    <div data-font-size-20 data-font-weight-600 data-color-000>
                        {mode === 'cover' ? '确认使用导入数据覆盖？' : '确认处理重复会员？'}
                    </div>
                    <div data-font-size-14 data-color-999 data-margin-top-8>
                        {mode === 'cover'
                            ? '系统会用导入数据覆盖重复会员的已有信息，处理后不可批量撤回。'
                            : '系统会按当前逐条选择处理重复会员；逐条使用导入基础数据不会覆盖手机号、生日、会员卡号，处理后不可批量撤回。'}
                    </div>
                </div>
            </SgModal>,
            { styles: confirmLayerStyles },
        )
    }

    if (!props.visible) return null

    return (
        <SgModal
            title="处理重复会员"
            isShow
            type="success"
            onClose={props.onCancel}
            headerStyles={repeatModalStyles}
            footer={
                <Row className="repeat-member__footer">
                    <Button types="primary" onClick={props.onCancel}>
                        取消
                    </Button>
                    <Button
                        types="primary"
                        loading={req.loading}
                        disabled={!currentRecordId || !duplicatedPairs.length}
                        onClick={() => submit('keep')}
                    >
                        全部保留已有会员
                    </Button>
                    <Button
                        types="primary"
                        loading={req.loading}
                        disabled={!currentRecordId || !duplicatedPairs.length}
                        onClick={() => submit('cover')}
                    >
                        全部使用导入数据
                    </Button>
                    <Button
                        types="default"
                        loading={req.loading}
                        disabled={!currentRecordId || !duplicatedPairs.length}
                        onClick={() => submit('custom')}
                    >
                        按当前选择处理
                    </Button>
                </Row>
            }
        >
            <div className="repeat-member__body">
                <Spin spinning={infoReq.loading}>
                    <MemberNotice className="repeat-member__notice" type="warning">
                        重复会员处理会影响会员余额、积分和历史消费统计；逐条使用导入基础数据不会覆盖手机号、生日、会员卡号，如需全量覆盖这些字段，请使用“全部使用导入数据”。
                    </MemberNotice>
                    <div className="repeat-member__tabs">
                        <Tab
                            tabList={duplicateTabs}
                            activeId={activeTab}
                            itemWidth={tabItemWidth}
                            onClick={item => setActiveTab(item.id)}
                        />
                    </div>
                    <div className="repeat-member__tab-content">
                        {activeTab === 0 ? (
                            <div className="table-container repeat-member__table">
                                <Table
                                    bordered
                                    rowKey={pair => pair.existed_member?.id || pair.imported_member?.id}
                                    columns={normalColumns}
                                    dataSource={duplicatedPairs}
                                    pagination={false}
                                />
                            </div>
                        ) : null}
                        {activeTab === 1 ? (
                            <>
                                <MemberNotice className="repeat-member__notice">
                                    卡号重复的数据不会直接写入会员卡号；请先核对冲突会员，处理后可在会员详情中单独调整卡号。
                                </MemberNotice>
                                <div className="table-container repeat-member__table">
                                    <Table
                                        bordered
                                        rowKey={pair => pair.imported_member?.id}
                                        columns={readonlyColumns}
                                        dataSource={cardCodePairs}
                                        pagination={false}
                                    />
                                </div>
                            </>
                        ) : null}
                        {activeTab === 2 ? (
                            <>
                                <MemberNotice className="repeat-member__notice">
                                    表格导入与老零售已导入数据重复时，不会重复创建会员。
                                </MemberNotice>
                                <div className="table-container repeat-member__table">
                                    <Table
                                        bordered
                                        rowKey={pair => pair.imported_member?.id}
                                        columns={readonlyColumns}
                                        dataSource={oldLsPairs}
                                        pagination={false}
                                    />
                                </div>
                            </>
                        ) : null}
                    </div>
                </Spin>
            </div>
        </SgModal>
    )
}

export default RepeatMemberHandler
